/**
 * Remove inline event-handler attributes (`onerror`, `onclick`, `onload`, …) and
 * `javascript:` URLs from a string of HTML.
 *
 * WHY THIS EXISTS
 * ---------------
 * `src/index.html` ships an enforcing CSP with `script-src-attr 'none'` and a
 * `script-src` that deliberately omits `'unsafe-inline'`. Any inline handler that
 * reaches the live DOM is blocked and logs:
 *
 *   "Executing inline event handler violates the following Content Security
 *    Policy directive: 'script-src-attr 'none''."
 *
 * Angular's sanitizer already strips handlers from `[innerHTML]` bindings, so
 * those are safe and are NOT the cause. The unsafe routes are the ones that skip
 * the sanitizer entirely:
 *
 *   1. direct `element.innerHTML = <server data>` — Quill editor roots, the
 *      signature contenteditable, calendar titles; and
 *   2. `DomSanitizer.bypassSecurityTrustHtml(<server data>)`.
 *
 * Note and comment HTML is authored in a rich-text editor and stored in the DB,
 * so a pasted `<img src=x onerror=…>` survives the round-trip and lands in the
 * DOM verbatim. Stripping at the render boundary removes the CSP violation and
 * closes the matching XSS hole, and keeps working even if the stored row is
 * never cleaned.
 *
 * BEHAVIOUR: removes only `on*` attributes and `javascript:` URLs. Text, tags,
 * classes, inline styles, spacing and every other attribute are preserved byte
 * for byte, so rendering is unchanged.
 *
 * The email module keeps its own `EmailService.stripScripts`, which does this
 * plus `<script>`/`<noscript>` removal for untrusted third-party email bodies.
 */
export function stripInlineHandlers(html: string): string {
  if (!html) return html || '';
  return html
    // on*="…" / on*='…' / on*=unquoted
    .replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son[a-z]+\s*=\s*'[^']*'/gi, '')
    .replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, '')
    // neutralise javascript: URLs in href/src
    .replace(/((?:href|src)\s*=\s*)("|')\s*javascript:[^"']*\2/gi, '$1$2#$2');
}
